import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import { toast } from 'react-toastify';
import api from '../services/api';
import { Search, Edit as EditIcon, Delete as DeleteIcon, List as ListIcon, Activity as AttivitaSvoltaLogoIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const StandardBadge = styled('div')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 36,
  height: 36,
  padding: '0 8px',
  borderRadius: 6,
  backgroundColor: 'rgba(255, 171, 0, 0.08)',
  color: '#FF9800',
  border: '1px solid rgba(255, 171, 0, 0.24)',
  fontWeight: 700,
  fontSize: '0.9rem',
  lineHeight: 1
}));

function AttivitaSvoltaInner() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/attivita/attivita-svolta');
      setRows(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = rows.filter(r => r.name.toLowerCase().includes(search.toLowerCase()));
  const grandTotal = filtered.reduce((sum, r) => sum + (Number(r.total_usage) || 0), 0);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sorted = [...filtered].sort((a, b) => {
    const valueA = a[orderBy];
    const valueB = b[orderBy];
    if (valueA === null || valueA === undefined) return 1;
    if (valueB === null || valueB === undefined) return -1;
    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return order === 'asc' ? valueA - valueB : valueB - valueA;
    }
    const strA = String(valueA).toLowerCase();
    const strB = String(valueB).toLowerCase();
    if (strA < strB) return order === 'asc' ? -1 : 1;
    if (strA > strB) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const handleOpenDialog = (row = null) => {
    if (row) {
      setCurrentId(row.id);
      setName(row.name || '');
    } else {
      setCurrentId(null);
      setName('');
    }
    setDialogOpen(true);
  };
  const handleCloseDialog = () => setDialogOpen(false);

  const handleSave = async () => {
    try {
      if (!name.trim()) {
        toast.error('Nome obbligatorio');
        return;
      }
      if (currentId) {
        await api.put(`/attivita/attivita-svolta/${currentId}`, { name });
        toast.success('Aggiornato');
      } else {
        await api.post('/attivita/attivita-svolta', { name });
        toast.success('Creato');
      }
      handleCloseDialog();
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error('Errore salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare?')) return;
    try {
      await api.delete(`/attivita/attivita-svolta/${id}`);
      toast.success('Eliminato');
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error('Errore eliminazione');
    }
  };

  const handleOpenDetail = (row) => {
    navigate(`/attivita/attivita-svolta/${row.id}`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <AttivitaSvoltaLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Attività Svolta
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Catalogo delle tipologie di attività eseguibili sulle pratiche
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" onClick={() => handleOpenDialog()} sx={{ borderRadius: 2, textTransform: 'none' }}>
          Nuovo
        </Button>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 300 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell sortDirection={orderBy === 'id' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleRequestSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'name' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Totale</TableCell>
                <TableCell align="right">% sul totale</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">Caricamento...</TableCell>
                </TableRow>
              ) : (
                sorted
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{row.name}</TableCell>
                      <TableCell align="right">
                        <StandardBadge
                          style={{
                            minWidth: 40,
                            height: 40,
                            fontSize: '0.95rem'
                          }}
                        >
                          {Number(row.total_usage) || 0}
                        </StandardBadge>
                      </TableCell>
                      <TableCell align="right">
                        {grandTotal > 0 ? (
                          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                            <CircularProgress
                              variant="determinate"
                              value={100}
                              size={36}
                              thickness={6}
                              sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                            />
                            <CircularProgress
                              variant="determinate"
                              value={((Number(row.total_usage) || 0) * 100) / grandTotal}
                              size={36}
                              thickness={6}
                              sx={{ position: 'absolute', left: 0 }}
                              color="warning"
                            />
                            <Box
                              sx={{
                                top: 0,
                                left: 0,
                                bottom: 0,
                                right: 0,
                                position: 'absolute',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.7rem' }}>
                                {(((Number(row.total_usage) || 0) * 100) / grandTotal).toFixed(2)}%
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          '0.00%'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenDialog(row)} color="primary">
                          <EditIcon size={18} />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleOpenDetail(row)} color="info">
                          <ListIcon size={18} />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(row.id)} color="error">
                          <DeleteIcon size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{currentId ? 'Modifica' : 'Nuovo'} Attività Svolta</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleSave} variant="contained">Salva</Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
}

export default function AttivitaSvolta() {
  const { user } = useAuth();
  const roleName = (user?.role_name || user?.role || '').toLowerCase();
  const isAdmin = roleName === 'admin';

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <AttivitaSvoltaInner />;
}
